'use client'

import { useProductCardContext } from '@/components/common/product-card/context/product-card-context'
import { TagThumbnail } from '@/components/ui/tag-thumbnail'
import { useTrackEvent } from '@/lib/gtm/track-event'
import { ETrackingEvent, TTrackingEcommerce } from '@/lib/gtm/type'
import { cn, formatPrice } from '@/lib/utils'
import { ProductFormatted } from '@/types/product'
import { useTranslations } from 'next-intl'
import Image from 'next/image'
import Link from 'next/link'
import { useMemo } from 'react'

// Mở rộng type ProductFormatted để thêm product_status
interface FlashSaleProduct extends ProductFormatted {
  product_status?: 'hot' | 'low_stock'
}

// Function to mask the second digit of the price with an asterisk
const maskDigit = (price: string): string => {
  if (!price) return price

  // Find the first digit and replace the second digit with *
  const parts = price.split('.')
  if (parts.length === 0) return price

  const firstPart = parts[0]
  if (firstPart.length < 2) return price

  // Replace the second digit with *
  const maskedFirstPart = firstPart.charAt(0) + '?' + firstPart.substring(2)

  // Reconstruct the price
  return parts.length > 1
    ? maskedFirstPart + '.' + parts.slice(1).join('.')
    : maskedFirstPart
}

interface Props {
  type: 'current' | 'next'
}

export const ProductItemInfo = ({ type }: Props) => {
  const { product: originalProduct, selectedColorVariant } =
    useProductCardContext()

  const t = useTranslations('collection.flash-sale')

  // Sử dụng type FlashSaleProduct
  const product = originalProduct as FlashSaleProduct

  const trackEvent = useTrackEvent<Partial<TTrackingEcommerce>>()

  const handleClick = () => {
    trackEvent({
      event: ETrackingEvent.SelectItem,
      ecommerce: {
        product_id: product.id,
        product_name: product.title,
        product_price: product.regular_price,
        product_quantity: 1,
        product_url: product.href,
        product_image: product.default_thumbnail,
        product_variant: selectedColorVariant?.color,
        product_discount: product.sale_percent,
        product_rating: product?.review?.ratingValue,
        items: [
          {
            item_id: product.id,
            item_name: product.title,
            price: product.regular_price,
            quantity: 1,
            item_brand: 'Coolmate',
            href: product.href,
            item_url: product.href,
            item_image: product.default_thumbnail,
            item_variant: selectedColorVariant?.color,
          },
        ],
      },
    })
  }

  // nếu status là hot thì random từ 50% đến 100% còn sắp hết thì từ 40%-10%
  // Sử dụng useMemo và deterministic hash để đảm bảo tính thuần khiết (purity) và tránh lỗi hydration
  const randomProgress = useMemo(() => {
    const id = String(product.id || '')
    let hash = 0
    for (let i = 0; i < id.length; i++) {
      hash = (hash << 5) - hash + id.charCodeAt(i)
      hash = hash & hash // Convert to 32bit integer
    }
    const pseudoRandom = Math.abs(hash % 1000) / 1000

    return product.product_status === 'hot'
      ? Math.floor(pseudoRandom * 51) + 50
      : Math.floor(pseudoRandom * 31) + 20
  }, [product.id, product.product_status])

  return (
    <figcaption className='flex flex-col gap-1 lg:gap-2'>
      {/* Product title */}
      <Link
        href={product?.href || '/'}
        className='w-fit'
        aria-label={`Xem chi tiết sản phẩm ${product?.title}`}
        ga-tracking-value={`flash-sale__${product?.id}`}
        ga-tracking-label={product?.title}
        onClick={handleClick}
        prefetch={false}
      >
        <h3 className='line-clamp-2 min-h-[40px] font-sans text-xs leading-3.5 text-neutral-800 hover:text-primary lg:min-h-[50px] lg:text-base lg:leading-5'>
          {product?.title}
        </h3>
      </Link>

      {/* Price section at the bottom */}
      <div className='mt-auto flex flex-wrap items-center gap-1'>
        <p
          className={cn(
            'font-sans text-xs font-bold leading-3.5 lg:text-base lg:leading-5',
            product?.sale_percent ? 'text-neutral-900' : 'text-neutral-400'
          )}
        >
          {type === 'current'
            ? formatPrice(product?.regular_price ?? 0)
            : maskDigit(formatPrice(product?.regular_price ?? 0))}
        </p>

        {product?.compare_price &&
        product.compare_price !== product.regular_price ? (
          <p className='text-xs leading-3.5 text-neutral-500 line-through lg:text-sm lg:leading-4.5'>
            {formatPrice(product?.compare_price ?? 0)}
          </p>
        ) : null}

        {/* Discount tag */}
        <div className='mb-auto'>
          <TagThumbnail
            title={
              type === 'current' && product?.sale_percent
                ? `-${product.sale_percent}%`
                : '??%'
            }
            variant='primary'
            size='small'
            className='px-1.5 text-xxs font-semibold leading-3 text-light lg:text-sm lg:leading-4.5'
          />
        </div>
      </div>
      {/* Status tag */}
      {product?.product_status && type === 'current' && (
        <div className='relative h-5 w-full rounded-full bg-primary-200'>
          {/* Progress background */}
          <div
            className='absolute left-0 top-0 h-full rounded-l-full'
            style={{
              background:
                'linear-gradient(90.83deg, #000000 -22.45%, #273CCD 19.66%, #0085ED 62.37%, #0085ED 96.18%)',
              width: `${randomProgress}%`,
            }}
          >
            <div className='absolute inset-0 rounded-l-full bg-gradient-to-r from-transparent to-black/10'></div>
          </div>

          {/* Content (always visible regardless of progress) */}
          <div className='absolute inset-0 flex items-center px-2'>
            <Image
              src='/icons/flash-sale.svg'
              alt='flash sale'
              width={24}
              height={28}
              className='absolute bottom-0 left-0 h-7 w-6'
            />
            <span className='ml-7 text-xs text-white md:text-sm'>
              {product.product_status === 'hot'
                ? t('hot_selling')
                : t('running_out')}
            </span>
          </div>
        </div>
      )}
    </figcaption>
  )
}
